/**
 * straightSkeleton.ts
 *
 * A from-scratch 2D straight-skeleton implementation for a simple polygon
 * with holes, following the vertex-bisector wavefront simulation of
 * Felkel & Obdržálek (1998) — the same structure as the well-known
 * `polyskel` reference.
 *
 * What a straight skeleton is: shrink every edge of the polygon inward,
 * parallel to itself, at unit speed. Each vertex slides along the angular
 * bisector of its two incident edges. The traces of all moving vertices
 * form the straight skeleton. Two events drive the shrink:
 *   - EDGE event  — a wavefront edge collapses to zero length; its two
 *     neighbours become adjacent.
 *   - SPLIT event — a reflex vertex crashes into a non-adjacent wavefront
 *     edge, splitting (or, across a hole boundary, merging) the wavefront.
 *
 * Holes need no special handling beyond winding: the outer boundary is
 * CCW, every hole is CW, and all rings share ONE event queue so split
 * events can connect the outer wavefront to the hole wavefronts. That is
 * exactly the "space between rooms and the outline" case — the building
 * outline is the outer ring, each room/cavity is a hole, and the skeleton
 * runs down the middle of the walls.
 *
 * Engine: a heap-driven simulation with lazy invalidation. Each active vertex
 * owns its single soonest event in a binary min-heap; popped events are skipped
 * when a referenced vertex has gone stale, so we never rebuild the whole event
 * set per step (the earlier O(n⁴) rebuild loop is gone). Event TIMES are
 * perpendicular offsets from the ORIGINAL (immutable) edge supporting lines,
 * which keeps them consistent and monotonic — measuring against moving edges is
 * what let near-degenerate events win and fling arcs out of bounds.
 *
 * Scope: simulation-grade, not exact-arithmetic. Hardened for the irregular,
 * many-vertex, near-degenerate inputs that point-cloud-derived floor plans
 * produce, via three guards: (1) reject near-parallel bisector/edge meetings
 * (the out-of-bounds source); (2) reject zero-travel splits (the micro-arc
 * cascade source); (3) a CONTAINMENT guard — the straight skeleton is provably
 * inside the wall region, so any candidate or output arc outside it is dropped
 * as an artifact. A hard per-run iteration budget guarantees termination.
 *
 * Perfectly-symmetric synthetic inputs can still skew the exact ridge geometry
 * (simultaneous-event ordering); real scans are never that symmetric. The
 * algorithm is expected to evolve — keep the event handlers small and isolated.
 */

/** A 2D point / vector. Plain object so this module stays THREE-free. */
export interface Vec2 {
  x: number;
  y: number;
}

/** One arc of the resulting skeleton: a straight segment between two points. */
export interface SkeletonArc {
  a: Vec2;
  b: Vec2;
}

/** Input ring: an ordered loop of points (not closed — do not repeat the first). */
export type Ring = Vec2[];

export interface StraightSkeletonInput {
  /** Outer boundary loop. Winding is normalized internally to CCW. */
  outer: Ring;
  /** Hole loops (rooms / cavities). Winding is normalized internally to CW. */
  holes?: Ring[];
}

// ---------------------------------------------------------------------------
// Small vector helpers (local; the pass adapts THREE.Vector3 ↔ Vec2).
// ---------------------------------------------------------------------------

const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
const scale = (a: Vec2, s: number): Vec2 => ({ x: a.x * s, y: a.y * s });
const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
/** 2D scalar cross product (z of the 3D cross). */
const cross = (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x;
const len = (a: Vec2): number => Math.hypot(a.x, a.y);
const norm = (a: Vec2): Vec2 => {
  const l = len(a);
  return l < 1e-12 ? { x: 0, y: 0 } : { x: a.x / l, y: a.y / l };
};
const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Distance from point `p` to segment `a`→`b`. */
const distToSegment = (p: Vec2, a: Vec2, b: Vec2): number => {
  const ab = sub(b, a);
  const l2 = dot(ab, ab);
  if (l2 < 1e-18) return dist(p, a);
  let t = dot(sub(p, a), ab) / l2;
  t = Math.max(0, Math.min(1, t));
  return dist(p, add(a, scale(ab, t)));
};

/**
 * Points within this distance are treated as the same location. Two wavefront
 * vertices that drift this close are merged rather than allowed to keep
 * splitting against each other — that cascade is what produces the dense tangle
 * of micro-arcs on thin-walled, near-coincident-corner floor plans. ~1 mm at
 * the metre scale these plans use.
 */
const EPS_POINT = 1e-3;

/**
 * Signed area (standard shoelace, Y-up): positive = CCW. Used to normalize
 * winding regardless of how the caller wound the ring.
 */
const signedArea = (ring: Ring): number => {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
};

/** Perpendicular (unsigned) distance from point `p` to the infinite line through `e0`→`e1`. */
const distToLine = (p: Vec2, e0: Vec2, e1: Vec2): number => {
  const d = norm(sub(e1, e0));
  if (d.x === 0 && d.y === 0) return dist(p, e0);
  // |cross(d, p - e0)| is the perpendicular distance for a unit d.
  return Math.abs(cross(d, sub(p, e0)));
};

/**
 * Intersect two rays/lines, each given as a point + direction. Returns the
 * intersection point, or null if (near-)parallel.
 */
const intersectLines = (p1: Vec2, d1: Vec2, p2: Vec2, d2: Vec2): Vec2 | null => {
  const denom = cross(d1, d2);
  if (Math.abs(denom) < 1e-12) return null;
  const diff = sub(p2, p1);
  const t = cross(diff, d2) / denom;
  return add(p1, scale(d1, t));
};

// ---------------------------------------------------------------------------
// Wavefront simulation (heap-driven, lazy invalidation).
//
// Design (Felkel & Obdržálek / polyskel, hardened for irregular floor plans):
//   - ORIGINAL EDGES are immutable supporting lines. Every event's "time" is
//     the perpendicular offset distance from such a line — which is consistent
//     and monotonic precisely because these lines only translate as the
//     wavefront shrinks, they never rotate. (Measuring against a moving edge is
//     what made the earlier version's times inconsistent and let near-
//     degenerate events win, sending arcs out of bounds.)
//   - Each active VERTEX owns a single "next event" (the soonest of its two
//     edge-collapse candidates and its best split). Events go into a binary
//     min-heap; popped events are skipped if any referenced vertex is stale
//     (lazy invalidation) — so we never rebuild the whole event set per step.
//   - Coincident vertices are merged, and a small EPS_TIME window treats events
//     at essentially the same offset as simultaneous, avoiding the micro-arc
//     cascade on thin walls / near-coincident corners.
//
// Complexity: O(n log n) heap ops × O(n) split scan per event ≈ O(n² log n),
// fine for floor-plan inputs and no longer the O(n⁴) rebuild-everything loop.
// ---------------------------------------------------------------------------

/**
 * An immutable original boundary edge: a supporting line plus the bisector rays
 * at its two endpoints. Split-event candidates are tested against these (the
 * wedge each edge sweeps), and event times are perpendicular distances to its
 * line.
 */
interface OriginalEdge {
  /** A point on the supporting line. */
  p0: Vec2;
  /** Unit direction along the edge (winding direction). */
  dir: Vec2;
  /** Endpoint A (= p0) and its outward bisector ray direction. */
  aOrigin: Vec2;
  aBisector: Vec2;
  /** Endpoint B and its outward bisector ray direction. */
  bOrigin: Vec2;
  bBisector: Vec2;
}

/** One active wavefront vertex (node of a circular doubly-linked LAV). */
interface Vertex {
  id: number;
  point: Vec2;
  prev: Vertex;
  next: Vertex;
  /** The original edges on either side of this vertex (fixed supporting lines). */
  edgeLeft: OriginalEdge;
  edgeRight: OriginalEdge;
  bisector: Vec2;
  isReflex: boolean;
  valid: boolean;
  /** Connected-component marker; vertices sharing a loop carry the same value. */
  loop: number;
}

/** A queued event. `time` is the wavefront offset at which it happens. */
interface Event {
  time: number;
  point: Vec2;
  /** 'edge' collapses vertex `va` with its neighbour `vb`; 'split' cuts `va`'s
   *  wavefront against original edge `opp`. */
  kind: 'edge' | 'split';
  va: Vertex;
  vb: Vertex | null;
  opp: OriginalEdge | null;
}

/** Bisector direction (unit) for a corner with incident edge directions. */
const cornerBisector = (inDir: Vec2, outDir: Vec2): { bisector: Vec2; isReflex: boolean } => {
  const left = norm(scale(inDir, -1)); // points back along the incoming edge
  const right = norm(outDir);
  // CCW ring: convex turns left (cross<0), reflex turns right (cross>0). Holes
  // are CW but their solid side reads with the same sign, so this is uniform.
  const isReflex = cross(left, right) > 0;
  let bis = add(left, right);
  if (len(bis) < 1e-9) {
    // Antiparallel (straight-through) — bisector is the inward edge normal.
    bis = { x: -outDir.y, y: outDir.x };
  }
  if (isReflex) bis = scale(bis, -1);
  return { bisector: norm(bis), isReflex };
};

/** Min-heap of events keyed by `time`. */
class EventHeap {
  private items: Event[] = [];
  get size(): number {
    return this.items.length;
  }
  push(e: Event): void {
    const a = this.items;
    a.push(e);
    let i = a.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (a[parent].time <= a[i].time) break;
      [a[parent], a[i]] = [a[i], a[parent]];
      i = parent;
    }
  }
  pop(): Event | undefined {
    const a = this.items;
    if (a.length === 0) return undefined;
    const top = a[0];
    const last = a.pop() as Event;
    if (a.length > 0) {
      a[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        let s = i;
        if (l < a.length && a[l].time < a[s].time) s = l;
        if (r < a.length && a[r].time < a[s].time) s = r;
        if (s === i) break;
        [a[s], a[i]] = [a[i], a[s]];
        i = s;
      }
    }
    return top;
  }
}

/**
 * Compute the straight skeleton of `outer` (with optional `holes`) and return
 * the skeleton arcs (interior segments). Boundary edges of the input polygon
 * are NOT returned — only the inner skeleton.
 */
export function computeStraightSkeleton(input: StraightSkeletonInput): SkeletonArc[] {
  // --- Normalize winding: outer CCW (area > 0), holes CW (area < 0). ---
  const outer = signedArea(input.outer) < 0 ? input.outer.slice().reverse() : input.outer.slice();
  const holes = (input.holes ?? []).map((h) => (signedArea(h) > 0 ? h.slice().reverse() : h.slice()));

  // Deduplicate consecutive coincident points (scan data often has them).
  const clean = (ring: Ring): Ring => {
    const out: Vec2[] = [];
    for (const p of ring) {
      const last = out[out.length - 1];
      if (!last || dist(last, p) > EPS_POINT) out.push({ x: p.x, y: p.y });
    }
    if (out.length >= 2 && dist(out[0], out[out.length - 1]) <= EPS_POINT) out.pop();
    return out;
  };

  const cleanOuter = clean(outer);
  const cleanHoles = holes.map(clean).filter((r) => r.length >= 3);
  const rings = [cleanOuter, ...cleanHoles].filter((r) => r.length >= 3);
  if (rings.length === 0) return [];

  // Ray-crossing point-in-polygon. A small outward margin (EPS_REGION) keeps
  // legitimate skeleton points that sit exactly on a shrinking edge from being
  // rejected, while still catching the far out-of-bounds excursions that
  // near-degenerate events produce on irregular scan data.
  const inPoly = (p: Vec2, poly: Ring): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x;
      const yi = poly[i].y;
      const xj = poly[j].x;
      const yj = poly[j].y;
      if (yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  const EPS_REGION = 0.05; // metres of slack outside the outline / inside holes
  /** True when `p` lies in the wall region: inside the outline, outside holes. */
  const pointInRegion = (p: Vec2): boolean => {
    if (cleanOuter.length >= 3 && !inPoly(p, cleanOuter)) {
      // Allow a hair outside (numerical slack on boundary-coincident events).
      let nearOuter = false;
      for (let i = 0; i < cleanOuter.length; i++) {
        const a = cleanOuter[i];
        const b = cleanOuter[(i + 1) % cleanOuter.length];
        if (distToSegment(p, a, b) <= EPS_REGION) {
          nearOuter = true;
          break;
        }
      }
      if (!nearOuter) return false;
    }
    for (const hole of cleanHoles) {
      if (inPoly(p, hole)) {
        // Inside a hole is only OK if essentially on its boundary.
        let nearHole = false;
        for (let i = 0; i < hole.length; i++) {
          const a = hole[i];
          const b = hole[(i + 1) % hole.length];
          if (distToSegment(p, a, b) <= EPS_REGION) {
            nearHole = true;
            break;
          }
        }
        if (!nearHole) return false;
      }
    }
    return true;
  };

  let vid = 0;
  let loopId = 0;
  const allEdges: OriginalEdge[] = [];
  const heads: Vertex[] = [];

  // --- Build the LAVs + original edges. ---
  for (const ring of rings) {
    const loop = loopId++;
    const n = ring.length;
    // Explicit loop (not `.map`) so the closure doesn't capture the mutable
    // `vid` counter — keeps eslint's no-loop-func happy.
    const verts: Vertex[] = [];
    for (const p of ring) {
      verts.push({
        id: vid++,
        point: { x: p.x, y: p.y },
        prev: null as unknown as Vertex,
        next: null as unknown as Vertex,
        edgeLeft: null as unknown as OriginalEdge,
        edgeRight: null as unknown as OriginalEdge,
        bisector: { x: 0, y: 0 },
        isReflex: false,
        valid: true,
        loop,
      });
    }
    // Edge i goes verts[i] → verts[i+1].
    const edges: OriginalEdge[] = [];
    for (let i = 0; i < n; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % n];
      edges.push({
        p0: { x: a.point.x, y: a.point.y },
        dir: norm(sub(b.point, a.point)),
        aOrigin: { x: a.point.x, y: a.point.y },
        aBisector: { x: 0, y: 0 },
        bOrigin: { x: b.point.x, y: b.point.y },
        bBisector: { x: 0, y: 0 },
      });
    }
    for (let i = 0; i < n; i++) {
      verts[i].prev = verts[(i - 1 + n) % n];
      verts[i].next = verts[(i + 1) % n];
      verts[i].edgeLeft = edges[(i - 1 + n) % n]; // edge ending at this vertex
      verts[i].edgeRight = edges[i]; // edge starting at this vertex
    }
    for (const v of verts) {
      const { bisector, isReflex } = cornerBisector(v.edgeLeft.dir, v.edgeRight.dir);
      v.bisector = bisector;
      v.isReflex = isReflex;
    }
    // Record each edge's endpoint bisectors (used by the split wedge test).
    for (let i = 0; i < n; i++) {
      edges[i].aBisector = verts[i].bisector;
      edges[i].bBisector = verts[(i + 1) % n].bisector;
    }
    allEdges.push(...edges);
    heads.push(verts[0]);
  }

  const arcs: SkeletonArc[] = [];
  const pushArc = (a: Vec2, b: Vec2): void => {
    if (dist(a, b) < EPS_POINT) return;
    arcs.push({ a: { x: a.x, y: a.y }, b: { x: b.x, y: b.y } });
  };

  // --- Per-vertex event computation. ---

  /** Soonest edge-collapse candidate of `v` with one neighbour `n`. */
  const edgeCandidate = (v: Vertex, n: Vertex): Event | null => {
    if (Math.abs(cross(v.bisector, n.bisector)) < 1e-6) return null; // parallel
    const ip = intersectLines(v.point, v.bisector, n.point, n.bisector);
    if (!ip) return null;
    // Must be forward along BOTH bisectors (a genuine convergence).
    if (dot(sub(ip, v.point), v.bisector) < EPS_POINT) return null;
    if (dot(sub(ip, n.point), n.bisector) < EPS_POINT) return null;
    const edge = n === v.next ? v.edgeRight : v.edgeLeft;
    const time = distToLine(ip, edge.p0, add(edge.p0, edge.dir));
    return { time, point: ip, kind: 'edge', va: v, vb: n, opp: null };
  };

  // Edges whose wavefront is still standing (some valid vertex has it as
  // `edgeRight`). A split can only target an ACTIVE edge — targeting one whose
  // wavefront was already consumed produces a stale event that strands the
  // splitting vertex (the dangling corners). Rebuilt lazily when the wavefront
  // changes (`edgesDirty`).
  let edgesDirty = true;
  const activeEdges = new Set<OriginalEdge>();
  const refreshActiveEdges = (): void => {
    if (!edgesDirty) return;
    activeEdges.clear();
    const seen = new Set<Vertex>();
    for (const head of heads) {
      let w: Vertex = head;
      let steps = 0;
      do {
        if (!seen.has(w)) {
          seen.add(w);
          if (w.valid) activeEdges.add(w.edgeRight);
        }
        w = w.next;
      } while (w !== head && ++steps < vid + 10);
    }
    edgesDirty = false;
  };

  /** Best split candidate of `v` against any non-incident, still-ACTIVE edge. */
  const splitCandidate = (v: Vertex): Event | null => {
    refreshActiveEdges();
    let best: Event | null = null;
    for (const e of allEdges) {
      if (e === v.edgeLeft || e === v.edgeRight) continue;
      if (!activeEdges.has(e)) continue; // wavefront for this edge is gone
      // Same-loop edges only matter for a reflex vertex; cross-loop edges (hole
      // merge) matter for every vertex. We don't track an edge's current loop,
      // so gate by reflexivity only: reflex vertices test ALL edges (covers both
      // self-fold and hole-merge); convex vertices test only when there is more
      // than one loop (a convex vertex can still meet another loop's edge).
      if (!v.isReflex && loopId <= 1) continue;

      const edgeP = e.p0;
      const edgeDir = e.dir;
      const eL = norm(scale(v.edgeLeft.dir, -1));
      const eR = norm(v.edgeRight.dir);
      const lineDir = Math.abs(dot(eL, edgeDir)) < Math.abs(dot(eR, edgeDir)) ? eL : eR;
      const i = intersectLines(edgeP, edgeDir, v.point, lineDir);
      if (!i) continue;
      let edvec = edgeDir;
      const linvec = norm(sub(v.point, i));
      if (dot(linvec, edvec) < 0) edvec = scale(edvec, -1);
      let bisecVec = add(norm(edvec), linvec);
      if (len(bisecVec) < 1e-9) continue;
      bisecVec = norm(bisecVec);
      if (Math.abs(cross(v.bisector, bisecVec)) < 1e-6) continue; // parallel → ∞
      const b = intersectLines(v.point, v.bisector, i, bisecVec);
      if (!b) continue;
      if (dot(sub(b, v.point), v.bisector) < EPS_POINT) continue; // not in future

      // Wedge test (winding-agnostic): b inside the slab the edge sweeps.
      const tol = 1e-7;
      const sameSide = (origin: Vec2, dir: Vec2, ref: Vec2, p: Vec2): boolean => {
        const sRef = cross(dir, sub(ref, origin));
        const sP = cross(dir, sub(p, origin));
        return Math.abs(sP) <= tol || sRef * sP > 0;
      };
      const leftOk = sameSide(e.aOrigin, e.aBisector, e.bOrigin, b);
      const rightOk = sameSide(e.bOrigin, e.bBisector, e.aOrigin, b);
      const sweepDir = norm(add(e.aBisector, e.bBisector));
      const edgeOk = dot(sub(b, edgeP), sweepDir) > tol;
      if (!(leftOk && rightOk && edgeOk)) continue;

      const time = distToLine(b, edgeP, add(edgeP, edgeDir));
      if (time < EPS_POINT) continue; // degenerate zero-travel split
      if (!best || time < best.time) best = { time, point: b, kind: 'split', va: v, vb: null, opp: e };
    }
    return best;
  };

  /** Compute & push `v`'s single soonest event. */
  const scheduleVertex = (v: Vertex): void => {
    let best: Event | null = null;
    const consider = (e: Event | null) => {
      // The straight skeleton is provably contained in the wall region. Any
      // candidate meeting OUTSIDE it is a near-degenerate artifact (the source
      // of the out-of-bounds runaways on irregular scan data) — drop it so it
      // can never be processed or spawn a cascade.
      if (e && !pointInRegion(e.point)) return;
      if (e && (!best || e.time < best.time)) best = e;
    };
    consider(edgeCandidate(v, v.prev));
    consider(edgeCandidate(v, v.next));
    consider(splitCandidate(v));
    if (best) heap.push(best);
  };

  const heap = new EventHeap();
  for (const head of heads) {
    let v: Vertex = head;
    do {
      scheduleVertex(v);
      v = v.next;
    } while (v !== head);
  }

  /** Walk a cycle from `start`, stamping a fresh loop id; returns visited set. */
  const stampLoop = (start: Vertex, id: number): Set<Vertex> => {
    const seen = new Set<Vertex>();
    let cur: Vertex = start;
    do {
      if (seen.has(cur)) break;
      seen.add(cur);
      cur.loop = id;
      cur = cur.next;
    } while (cur !== start);
    return seen;
  };

  // --- Main event loop. ---
  // Hard iteration budget so a pathological input can't hang the UI. Sized
  // generously relative to the vertex count; real plans finish far below it.
  const budget = vid * 40 + 1000;
  let processed = 0;

  while (heap.size > 0 && processed < budget) {
    const ev = heap.pop();
    if (!ev) break;
    processed++;
    const v = ev.va;
    if (!v.valid) continue; // stale
    // This event will mutate the wavefront — the active-edge set must be rebuilt
    // before the next split candidate is computed.
    edgesDirty = true;

    if (ev.kind === 'edge') {
      const n = ev.vb;
      if (!n || !n.valid) continue;
      if (v.next !== n && v.prev !== n) continue; // no longer adjacent — stale

      // Order so first.next === second.
      const first = v.next === n ? v : n;
      const second = v.next === n ? n : v;
      if (first.next !== second) continue;

      // Triangle collapse: a 3-vertex ring vanishes to a point.
      if (first.prev === second.next) {
        const third = second.next;
        pushArc(first.point, ev.point);
        pushArc(second.point, ev.point);
        if (third.valid) pushArc(third.point, ev.point);
        first.valid = second.valid = third.valid = false;
        continue;
      }

      pushArc(first.point, ev.point);
      pushArc(second.point, ev.point);
      const merged: Vertex = {
        id: vid++,
        point: { x: ev.point.x, y: ev.point.y },
        prev: first.prev,
        next: second.next,
        edgeLeft: first.edgeLeft,
        edgeRight: second.edgeRight,
        bisector: { x: 0, y: 0 },
        isReflex: false,
        valid: true,
        loop: first.loop,
      };
      first.prev.next = merged;
      second.next.prev = merged;
      const cb = cornerBisector(merged.edgeLeft.dir, merged.edgeRight.dir);
      merged.bisector = cb.bisector;
      merged.isReflex = cb.isReflex;
      first.valid = second.valid = false;
      for (let i = 0; i < heads.length; i++) if (heads[i] === first || heads[i] === second) heads[i] = merged;
      scheduleVertex(merged);
      continue;
    }

    // --- Split event. ---
    const opp = ev.opp;
    if (!opp) continue;
    // The opposite edge may have been consumed; find a CURRENT vertex pair whose
    // shared edge is still `opp` (its start vertex's edgeRight === opp).
    let uStart: Vertex | null = null;
    for (const head of heads) {
      let w: Vertex = head;
      let steps = 0;
      do {
        if (w.valid && w.edgeRight === opp) {
          uStart = w;
          break;
        }
        w = w.next;
      } while (w !== head && ++steps < vid + 10);
      if (uStart) break;
    }
    // The opposite edge was consumed before this split could run — the event is
    // stale. Don't DROP the vertex (that left corners dangling); recompute its
    // soonest event against the current wavefront and requeue it.
    if (!uStart || !uStart.valid) {
      scheduleVertex(v);
      continue;
    }
    const uEnd = uStart.next;
    if (!uEnd.valid) {
      scheduleVertex(v);
      continue;
    }
    // v must still be distinct from the edge endpoints — if not, requeue.
    if (v === uStart || v === uEnd) {
      scheduleVertex(v);
      continue;
    }

    pushArc(v.point, ev.point);

    const v1: Vertex = {
      id: vid++,
      point: { x: ev.point.x, y: ev.point.y },
      prev: v.prev,
      next: uEnd,
      edgeLeft: v.edgeLeft,
      edgeRight: opp,
      bisector: { x: 0, y: 0 },
      isReflex: false,
      valid: true,
      loop: -1,
    };
    const v2: Vertex = {
      id: vid++,
      point: { x: ev.point.x, y: ev.point.y },
      prev: uStart,
      next: v.next,
      edgeLeft: opp,
      edgeRight: v.edgeRight,
      bisector: { x: 0, y: 0 },
      isReflex: false,
      valid: true,
      loop: -1,
    };
    v.prev.next = v1;
    uEnd.prev = v1;
    uStart.next = v2;
    v.next.prev = v2;
    const cb1 = cornerBisector(v1.edgeLeft.dir, v1.edgeRight.dir);
    v1.bisector = cb1.bisector;
    v1.isReflex = cb1.isReflex;
    const cb2 = cornerBisector(v2.edgeLeft.dir, v2.edgeRight.dir);
    v2.bisector = cb2.bisector;
    v2.isReflex = cb2.isReflex;
    v.valid = false;

    const reached = stampLoop(v1, loopId++);
    if (!reached.has(v2)) stampLoop(v2, loopId++);

    for (let i = 0; i < heads.length; i++) if (heads[i] === v) heads[i] = v1;
    heads.push(v2);
    scheduleVertex(v1);
    scheduleVertex(v2);
  }

  // --- Residual flush: close out SMALL wavefront loops still standing. ---
  // A genuine final collapse leaves a tiny loop (a few vertices spanning a
  // fraction of a metre). Collapse those to their centroid so the spine reaches
  // its true ridge node. A LARGE surviving loop, by contrast, means the sim gave
  // up on that region (degenerate input) — centroid-collapsing it would draw
  // long spurious chords across a room (the slivers the user circled), so we
  // leave it rather than fabricate geometry.
  {
    const visited = new Set<Vertex>();
    const walkLoop = (start: Vertex): Vertex[] => {
      const ring: Vertex[] = [];
      let cur: Vertex = start;
      let steps = 0;
      do {
        if (visited.has(cur)) break;
        visited.add(cur);
        if (cur.valid) ring.push(cur);
        cur = cur.next;
      } while (cur !== start && ++steps < vid + 10);
      return ring;
    };
    const FLUSH_MAX_SPAN = 1.0; // metres — a tiny loop collapses to one node
    for (const head of heads) {
      if (!head.valid || visited.has(head)) continue;
      const ring = walkLoop(head);
      if (ring.length === 0) continue;
      let minx = Infinity;
      let miny = Infinity;
      let maxx = -Infinity;
      let maxy = -Infinity;
      let cx = 0;
      let cy = 0;
      for (const v of ring) {
        cx += v.point.x;
        cy += v.point.y;
        minx = Math.min(minx, v.point.x);
        miny = Math.min(miny, v.point.y);
        maxx = Math.max(maxx, v.point.x);
        maxy = Math.max(maxy, v.point.y);
      }
      if (Math.max(maxx - minx, maxy - miny) <= FLUSH_MAX_SPAN) {
        // Small leftover loop → collapse to its centroid (one ridge node).
        const c = { x: cx / ring.length, y: cy / ring.length };
        for (const v of ring) pushArc(v.point, c);
      } else {
        // A larger loop never fully collapsed (degenerate / simultaneous-event
        // region). Wire each remaining vertex in along the WALL by emitting the
        // meet of its bisector with its next neighbour's (what the edge event
        // would have produced) — never a straight chord across the loop. Any
        // result that still crosses open space is dropped by the filter below.
        for (const v of ring) {
          const meet = intersectLines(v.point, v.bisector, v.next.point, v.next.bisector);
          if (meet && dot(sub(meet, v.point), v.bisector) > 0) pushArc(v.point, meet);
          else pushArc(v.point, { x: (v.point.x + v.next.point.x) / 2, y: (v.point.y + v.next.point.y) / 2 });
        }
      }
      for (const v of ring) v.valid = false;
    }
  }

  // Final containment guard: drop any arc that strays outside the wall region
  // (its midpoint is the cheap, reliable representative). The skeleton is
  // provably contained, so anything outside is an artifact. We also drop arcs
  // whose body passes through open room space (a chord across a room interior) —
  // those are spurious cross-room connections, never real wall centerlines.
  const result = arcs.filter((a) => {
    // Reject if ANY point along the arc — endpoints included — leaves the wall
    // region (outside the outline or inside a room). Sampling endpoints too is
    // what catches a flushed arc that reaches into a room interior.
    for (let i = 0; i <= 8; i++) {
      const t = i / 8;
      const p = { x: a.a.x + (a.b.x - a.a.x) * t, y: a.a.y + (a.b.y - a.a.y) * t };
      if (!pointInRegion(p)) return false;
    }
    return true;
  });

  return mergeColinearArcs(dedupeArcs(result));
}

/** Drop exact-duplicate arcs (same endpoints either orientation). */
const dedupeArcs = (arcs: SkeletonArc[]): SkeletonArc[] => {
  const seen = new Set<string>();
  const key = (a: Vec2, b: Vec2) => {
    const ax = a.x.toFixed(4);
    const ay = a.y.toFixed(4);
    const bx = b.x.toFixed(4);
    const by = b.y.toFixed(4);
    return ax < bx || (ax === bx && ay <= by) ? `${ax},${ay}|${bx},${by}` : `${bx},${by}|${ax},${ay}`;
  };
  const out: SkeletonArc[] = [];
  for (const arc of arcs) {
    const k = key(arc.a, arc.b);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(arc);
  }
  return out;
};

/**
 * Merge arcs that meet at a degree-2 point and are collinear into a single
 * arc — the simulation emits one short arc per shrink step along a straight
 * skeleton spine, which reads as a dotted line otherwise. Light cleanup only:
 * a point shared by exactly two arcs that run in (nearly) the same line is
 * dissolved, joining the two arcs end to end.
 */
const mergeColinearArcs = (arcs: SkeletonArc[]): SkeletonArc[] => {
  const keyOf = (p: Vec2) => `${p.x.toFixed(4)},${p.y.toFixed(4)}`;
  // endpoint key → arcs touching it
  const adj = new Map<string, SkeletonArc[]>();
  const touch = (k: string, arc: SkeletonArc) => {
    const list = adj.get(k) ?? [];
    list.push(arc);
    adj.set(k, list);
  };
  for (const arc of arcs) {
    touch(keyOf(arc.a), arc);
    touch(keyOf(arc.b), arc);
  }

  const removed = new Set<SkeletonArc>();

  /** The far endpoint of `arc` relative to the shared point `p`. */
  const farEnd = (arc: SkeletonArc, p: Vec2): Vec2 => (dist(arc.a, p) < dist(arc.b, p) ? arc.b : arc.a);

  for (const arc of arcs) {
    if (removed.has(arc)) continue;
    // Grow this arc forward through collinear degree-2 joints at its `b` end.
    let changed = true;
    while (changed) {
      changed = false;
      const k = keyOf(arc.b);
      const alive = (adj.get(k) ?? []).filter((x) => x !== arc && !removed.has(x));
      if (alive.length !== 1) break; // not a clean degree-2 joint
      const nxt = alive[0];
      const other = farEnd(nxt, arc.b);
      const d1 = norm(sub(arc.b, arc.a));
      const d2 = norm(sub(other, arc.b));
      if (Math.abs(cross(d1, d2)) >= 1e-4 || dot(d1, d2) <= 0) break; // not collinear/forward
      removed.add(nxt);
      arc.b = { x: other.x, y: other.y };
      touch(keyOf(arc.b), arc);
      changed = true;
    }
  }

  return arcs.filter((a) => !removed.has(a));
};
